<template>
  <div class="portfolio-transition-preview-step">
    <div v-if="applying" class="portfolio-transition-preview-step__applying">
      <div class="portfolio-transition-preview-step__applying-inner">
        <div class="text-h6 font-weight-medium text-center mb-6">
          İşlemler Uygulanıyor
        </div>

        <v-progress-linear
          :value="applyProgressPercent"
          height="6"
          color="primary"
          class="mb-4"
        />

        <div class="text-subtitle-1 font-weight-medium text-center mb-2">
          Toplam İşlenen Kayıt {{ formatInteger(applyProcessedCount) }} /
          {{ formatInteger(applyTotalCount) }}
        </div>

        <div class="text-body-2 text--secondary text-center mb-2">
          Başarılı: <strong>{{ formatInteger(applySuccessCount) }}</strong> |
          Başarısız: <strong>{{ formatInteger(applyFailedCount) }}</strong>
        </div>

        <div class="text-body-2 text--secondary text-center">
          Şu Anda İşlemde
          <strong>{{ applyCurrentLabel || "-" }}</strong>
        </div>

        <v-sheet
          v-if="applyCurrentRow"
          outlined
          rounded
          class="portfolio-transition-preview-step__current-summary mt-6 pa-4"
        >
          <div class="portfolio-transition-preview-step__summary-list mb-4">
            <div class="portfolio-transition-preview-step__summary-row">
              <div class="text-caption text--secondary">Müşteri Adı</div>
              <div class="font-weight-medium">
                {{ applyCurrentRow.customer_name || "-" }}
              </div>
            </div>

            <div class="portfolio-transition-preview-step__summary-row">
              <div class="text-caption text--secondary">İlgili TYA Bilgisi</div>
              <div class="font-weight-medium">
                {{ applyCurrentRow.cluster_label || "-" }}
              </div>
            </div>

            <div class="portfolio-transition-preview-step__summary-row">
              <div class="text-caption text--secondary">Ürün Bilgisi</div>
              <div class="font-weight-medium">
                {{ resolveApplyProductInfo(applyCurrentRow) }}
              </div>
            </div>

            <div class="portfolio-transition-preview-step__summary-row">
              <div class="text-caption text--secondary">
                Faturalandırma Tipi
              </div>
              <div class="font-weight-medium">
                {{ applyCurrentRow.billing_type || "-" }}
              </div>
            </div>

            <div class="portfolio-transition-preview-step__summary-row">
              <div class="text-caption text--secondary">Yenileme Kuralı</div>
              <div class="font-weight-medium">
                {{ applyCurrentRow.resolved_rule_label || "-" }}
              </div>
            </div>
          </div>

          <v-row dense>
            <v-col cols="12" md="6">
              <div class="text-caption text--secondary text-center mb-1">
                Mevcut Ürün
              </div>
              <div class="font-weight-medium text-center">
                {{ applyCurrentRow.before_summary_label || "-" }}
              </div>
              <div class="text-caption text--secondary text-center">
                {{ applyCurrentRow.current_period_label || "-" }}
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-caption text--secondary text-center mb-1">
                Planlanan Ürün
              </div>
              <div class="font-weight-medium text-center">
                {{ applyCurrentRow.after_summary_label || "-" }}
              </div>
              <div class="text-caption text--secondary text-center">
                {{ applyCurrentRow.next_period_label || "-" }}
              </div>
            </v-col>
          </v-row>
        </v-sheet>
      </div>
    </div>

    <template v-else>
      <v-alert type="info" outlined dense class="mb-4">
        Bu adım doğrulanmış önizlemeyi gösterir. İşleme uygun kayıtlar
        uygulanır; karar bekleyen ve bloklu kayıtlar sonuç ekranında atlandı
        olarak raporlanır.
      </v-alert>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <template v-if="previewData">
        <v-row dense class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-sheet outlined rounded class="pa-3">
              <div class="text-caption text--secondary">İşleme Hazır</div>
              <div class="text-h5 font-weight-bold">
                {{ formatInteger(meta.preview_ready_total) }}
              </div>
            </v-sheet>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-sheet outlined rounded class="pa-3">
              <div class="text-caption text--secondary">Karar Bekleyen</div>
              <div class="text-h5 font-weight-bold warning--text">
                {{ formatInteger(meta.needs_decision_total) }}
              </div>
            </v-sheet>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-sheet outlined rounded class="pa-3">
              <div class="text-caption text--secondary">Bloklu</div>
              <div class="text-h5 font-weight-bold error--text">
                {{ formatInteger(meta.blocked_total) }}
              </div>
            </v-sheet>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-sheet outlined rounded class="pa-3">
              <div class="text-caption text--secondary">Uyarılı Kayıt</div>
              <div class="text-h5 font-weight-bold">
                {{ formatInteger(meta.warning_total) }}
              </div>
            </v-sheet>
          </v-col>
        </v-row>

        <v-card
          outlined
          class="mb-4 portfolio-transition-preview-card portfolio-transition-preview-card--success"
        >
          <v-card-title
            class="portfolio-transition-preview-card__header"
            role="button"
            tabindex="0"
            @click="toggleSection('preview')"
            @keydown.enter.prevent="toggleSection('preview')"
            @keydown.space.prevent="toggleSection('preview')"
          >
            <div class="portfolio-transition-preview-card__header-copy">
              <div class="portfolio-transition-preview-card__title">
                İşleme Uygun Kayıtlar
              </div>
              <div class="portfolio-transition-preview-card__subtitle">
                Bu operasyonda uygulanabilecek kayıtlar
              </div>
            </div>

            <v-spacer />

            <v-icon
              large
              class="portfolio-transition-preview-card__toggle-icon"
              :class="{
                'portfolio-transition-preview-card__toggle-icon--open':
                  sections.preview,
              }"
            >
              mdi-chevron-down
            </v-icon>
          </v-card-title>

          <v-expand-transition>
            <div v-show="sections.preview">
              <v-data-table
                class="portfolio-transition-preview-table"
                :headers="previewHeaders"
                :items="previewRows"
                dense
                :items-per-page="-1"
                mobile-breakpoint="0"
                no-data-text="Önizlemeye girecek kayıt bulunmuyor."
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.customer_name="{ item }">
                  <div class="font-weight-medium">
                    {{ item.customer_name || "-" }}
                  </div>
                  <div class="text-caption text--secondary">
                    {{ item.cluster_label || "-" }}
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.before_summary_label="{ item }">
                  <div>{{ item.before_summary_label || "-" }}</div>
                  <div class="text-caption text--secondary">
                    {{ item.current_period_label || "-" }}
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.after_summary_label="{ item }">
                  <div>{{ item.after_summary_label || "-" }}</div>
                  <div class="text-caption text--secondary">
                    {{ item.next_period_label || "-" }}
                  </div>
                </template>

                <!-- eslint-disable-next-line -->
                <template v-slot:item.warnings="{ item }">
                  <div v-if="item.warnings && item.warnings.length">
                    <v-chip small outlined color="warning" class="mb-1">
                      {{ item.warnings.length }} uyarı
                    </v-chip>
                    <div class="text-caption text--secondary">
                      {{ item.warnings[0] }}
                    </div>
                  </div>
                  <span v-else class="text--secondary">-</span>
                </template>
              </v-data-table>
            </div>
          </v-expand-transition>
        </v-card>

        <v-card
          outlined
          v-if="blockedRows.length || needsDecisionRows.length"
          class="mb-4 portfolio-transition-preview-card portfolio-transition-preview-card--warning"
        >
          <v-card-title
            class="portfolio-transition-preview-card__header"
            role="button"
            tabindex="0"
            @click="toggleSection('nonPreview')"
            @keydown.enter.prevent="toggleSection('nonPreview')"
            @keydown.space.prevent="toggleSection('nonPreview')"
          >
            <div class="portfolio-transition-preview-card__header-copy">
              <div class="portfolio-transition-preview-card__title">
                İşleme Uygun Olmayan Kayıtlar
              </div>
              <div class="portfolio-transition-preview-card__subtitle">
                Bloklanan veya ek karar gerektiren kayıtlar
              </div>
            </div>

            <v-spacer />

            <v-icon
              large
              class="portfolio-transition-preview-card__toggle-icon"
              :class="{
                'portfolio-transition-preview-card__toggle-icon--open':
                  sections.nonPreview,
              }"
            >
              mdi-chevron-down
            </v-icon>
          </v-card-title>

          <v-expand-transition>
            <div v-show="sections.nonPreview">
              <v-data-table
                class="portfolio-transition-preview-table"
                :headers="nonPreviewHeaders"
                :items="nonPreviewRows"
                dense
                :items-per-page="-1"
                mobile-breakpoint="0"
                no-data-text="İşleme girmeyecek satır bulunmuyor."
              >
                <!-- eslint-disable-next-line -->
                <template v-slot:item.preview_status="{ item }">
                  <v-chip
                    small
                    outlined
                    :color="
                      item.preview_status === 'blocked' ? 'error' : 'warning'
                    "
                  >
                    {{
                      item.preview_status === "blocked"
                        ? "Bloklu"
                        : "Karar Bekliyor"
                    }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
          </v-expand-transition>
        </v-card>
      </template>

      <v-card outlined v-else-if="!loading">
        <v-card-text>
          <v-alert type="warning" outlined dense class="mb-0">
            Önizleme henüz hazırlanmadı.
          </v-alert>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
export default {
  name: "PortfolioYearEndTransitionPreviewStep",
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    applying: {
      type: Boolean,
      required: false,
      default: false,
    },
    applyProcessedCount: {
      type: Number,
      required: false,
      default: 0,
    },
    applyTotalCount: {
      type: Number,
      required: false,
      default: 0,
    },
    applyCurrentLabel: {
      type: String,
      required: false,
      default: null,
    },
    applyCurrentRow: {
      type: Object,
      required: false,
      default: null,
    },
    applySuccessCount: {
      type: Number,
      required: false,
      default: 0,
    },
    applyFailedCount: {
      type: Number,
      required: false,
      default: 0,
    },
    previewData: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      sections: {
        preview: true,
        nonPreview: true,
      },
      previewHeaders: [
        {
          text: "Müşteri",
          value: "customer_name",
          sortable: false,
          align: "start",
        },
        {
          text: "Önce",
          value: "before_summary_label",
          sortable: false,
          align: "start",
        },
        {
          text: "Sonra",
          value: "after_summary_label",
          sortable: false,
          align: "start",
        },
        {
          text: "Uyarı",
          value: "warnings",
          sortable: false,
          width: "110px",
          align: "start",
        },
      ],
      nonPreviewHeaders: [
        {
          text: "Müşteri",
          value: "customer_name",
          sortable: false,
          align: "start",
        },
        {
          text: "Durum",
          value: "preview_status",
          sortable: false,
          width: "140px",
          align: "start",
        },
        {
          text: "Sebep",
          value: "preview_reason",
          sortable: false,
          align: "start",
        },
      ],
    };
  },
  computed: {
    meta() {
      return (
        this.previewData?.meta || {
          can_apply: false,
          preview_ready_total: 0,
          needs_decision_total: 0,
          blocked_total: 0,
          skipped_total: 0,
          warning_total: 0,
        }
      );
    },
    previewRows() {
      return this.previewData?.preview_rows || [];
    },
    blockedRows() {
      return this.previewData?.blocked_rows || [];
    },
    needsDecisionRows() {
      return this.previewData?.needs_decision_rows || [];
    },
    nonPreviewRows() {
      return [...this.needsDecisionRows, ...this.blockedRows];
    },
    applyProgressPercent() {
      if (this.applyTotalCount < 1) {
        return 0;
      }

      return (this.applyProcessedCount / this.applyTotalCount) * 100;
    },
  },
  methods: {
    toggleSection(key) {
      this.$set(this.sections, key, !this.sections[key]);
    },
    formatInteger(value) {
      return new Intl.NumberFormat("tr-TR", {
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    resolveApplyProductInfo(row) {
      if (!row) {
        return "-";
      }

      return (
        row.current_product_name ||
        row.resolved_product_name ||
        row.before_summary_label ||
        row.after_summary_label ||
        "-"
      );
    },
  },
};
</script>

<style scoped>
.portfolio-transition-preview-card {
  overflow: hidden;
}

.portfolio-transition-preview-card__header {
  cursor: pointer;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
  align-items: center;
}

.portfolio-transition-preview-card__header-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.portfolio-transition-preview-card__title {
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 600;
}

.portfolio-transition-preview-card__subtitle {
  font-size: 0.875rem;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.6);
}

.portfolio-transition-preview-card--success {
  border-color: rgba(76, 175, 80, 0.16) !important;
}

.portfolio-transition-preview-card--warning {
  border-color: rgba(255, 152, 0, 0.16) !important;
}

.portfolio-transition-preview-card--success
  .portfolio-transition-preview-card__header {
  background: rgba(76, 175, 80, 0.1);
}

.portfolio-transition-preview-card--warning
  .portfolio-transition-preview-card__header {
  background: rgba(255, 152, 0, 0.1);
}

.portfolio-transition-preview-card__toggle-icon {
  transition: transform 0.2s ease;
}

.portfolio-transition-preview-card__toggle-icon--open {
  transform: rotate(180deg);
}

.portfolio-transition-preview-step {
  min-height: 100%;
}

.portfolio-transition-preview-step__applying {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.portfolio-transition-preview-step__applying-inner {
  width: 100%;
  max-width: 720px;
}

.portfolio-transition-preview-step__current-summary {
  background: #fff;
  border: 1px solid rgba(63, 81, 181, 0.12) !important;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.portfolio-transition-preview-step__summary-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.portfolio-transition-preview-step__summary-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}

.portfolio-transition-preview-table :deep(th) {
  text-align: left !important;
}

.portfolio-transition-preview-table :deep(th .v-data-table-header__content) {
  justify-content: flex-start !important;
  text-align: left !important;
}
</style>
